import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  DASHBOARD_PERIODS,
  fromMillidinars,
  type DashboardPeriod,
  type PeriodSummary,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { SalesChart } from './SalesChart';
import {
  CashDrawerPanel,
  CashInBoxesPanel,
  ComparisonHint,
  DeviceUsagePanel,
  estimateLeavesOutServiceCharge,
  EstimatedSalesTile,
  FloorGrid,
  formatClock,
  LowStockPanel,
  MonthlyTrendPanel,
  Panel,
  RefillsTile,
  ServiceChargeHint,
  StatTile,
  TargetPanel,
  TopCustomersPanel,
  TopDevicesPanel,
  useMoney,
} from './DashboardPanels';
import { useDashboard } from './useDashboard';

/**
 * Dashboard — SPEC §5.1, the owner's landing screen.
 *
 * One `GET /dashboard?period=…` paints the whole screen; nothing here recomputes
 * a figure. Everything the owner reads is a field of `DashboardPayload`, which
 * shares its arithmetic with the Overall Report (§5.7) precisely so the two
 * screens cannot disagree when they are read a minute apart.
 *
 * The DAY/MONTH toggle is the only control. It re-requests the payload, which
 * changes the chart's buckets and moves the emphasis between the daily and
 * monthly summaries — both stay on screen either way, because an owner
 * comparing today against the month should not have to toggle to do it.
 *
 * Nothing on this screen names the venue or its currency: the symbol arrives on
 * the payload and the branding lives in the app shell, because every venue on
 * this product is a different tenant.
 */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { can, venue } = useAuth();

  const [period, setPeriod] = useState<DashboardPeriod>('DAY');
  const { data, loading, error, reload } = useDashboard(period);

  // The payload carries the venue's symbol; the session's is only a stand-in
  // for the frames before the first response lands.
  const currencySymbol = data?.currencySymbol ?? venue?.currencySymbol ?? '';
  const money = useMoney(currencySymbol);

  if (!can('dashboard.view')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('dashboard.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('dashboard.noViewPermission')}</p>
      </div>
    );
  }

  const active: PeriodSummary | null = data ? (period === 'DAY' ? data.day : data.month) : null;
  const periodLabel = t(period === 'DAY' ? 'dashboard.period.day' : 'dashboard.period.month');

  // Zero sales is a normal reading — a fresh venue, or 10am. Say what is true
  // rather than leaving a wall of zeroes to be read as a broken screen.
  const noTradeYet = active !== null && fromMillidinars(active.salesMd) === 0n;

  // Service charge rides beside Sales, never inside it — and so it is not in
  // Estimated sales either, settled or running (a running sitting's charge is
  // not known until checkout, where it may be waived). Said on the card
  // whenever the venue charges one; a venue that charges none sees no note.
  const chargesService =
    data !== null &&
    estimateLeavesOutServiceCharge(data.serviceChargePercent, data.day.serviceChargeMd);

  return (
    <div className="flex h-full flex-col overflow-auto">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('dashboard.title')}</h1>

        <div className="flex-1" />

        {data ? (
          <span className="text-xs text-content-muted">
            {t('dashboard.businessDate')}{' '}
            <span className="tabular text-content-secondary">{data.businessDate}</span>
            {/* `emptyDashboardPayload` stamps the epoch, so an implausible
                timestamp means "never computed" and the caption is dropped
                rather than claiming the figures were refreshed in 1970. */}
            {isPlausibleInstant(data.generatedAt) ? (
              <>
                {' · '}
                {t('dashboard.updatedAt', {
                  time: formatClock(data.generatedAt, i18n.language),
                })}
              </>
            ) : null}
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:opacity-40"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          {t('dashboard.refresh')}
        </button>
      </header>

      {/* A failed poll keeps the last good payload on screen and warns above it;
          only a first load with nothing to show takes over the page. */}
      {error && data ? (
        <p role="alert" className="mx-4 mb-2 text-sm text-danger">
          {t('dashboard.error.stale')} <span className="text-content-muted">{error}</span>
        </p>
      ) : null}

      {error && !data ? (
        <ErrorState detail={error} onRetry={() => void reload()} />
      ) : !data || !active ? (
        <p className="p-4 text-sm text-content-muted">{t('common.loading')}</p>
      ) : (
        <div className="min-h-0 flex-1 space-y-3.5 px-4 pb-6">
          {/*
            The approved card's four money lines, all fields of the ACTIVE
            period summary: takings leads and glows, refills reads green.
            Net profit and expenses live on the Overall report, where their
            deductions are itemised rather than floating context-free.
          */}
          {/* Daily and monthly sales are BOTH always on screen (owner request):
              comparing today against the month must not require the toggle.
              The toggle still moves the emphasis and re-buckets the chart. */}
          <div className="grid grid-cols-2 gap-3.5 xl:grid-cols-7">
            {/* Owner request, 2026-09-20: what the day is worth RIGHT NOW,
                including the sittings nobody has stopped yet. It leads the row
                because it is the figure that answers "how are we doing" during
                a shift; Daily sales beside it is the half that has actually
                been taken, and the two are deliberately not merged. */}
            <EstimatedSalesTile
              estimate={data.estimatedSales}
              label={t('dashboard.kpi.estimatedSales')}
              money={money}
              /* Zero gets its own sentence rather than "includes 0 sittings":
                 on a closed floor the estimate IS the day's sales, and saying
                 so is the difference between a figure that reads as idle and
                 one that reads as broken. */
              hint={
                <>
                  {data.estimatedSales.runningSessions === 0
                    ? t('dashboard.kpi.estimatedSalesIdle')
                    : t('dashboard.kpi.estimatedSalesHint', {
                        count: data.estimatedSales.runningSessions,
                      })}
                  {chargesService ? (
                    <span className="block text-content-muted">
                      {t('dashboard.kpi.estimatedSalesNoServiceCharge')}
                    </span>
                  ) : null}
                </>
              }
            />
            <StatTile
              label={t('dashboard.kpi.dailySales')}
              value={money(data.day.salesMd)}
              emphasised={period === 'DAY'}
              hint={
                <>
                  <ComparisonHint
                    comparison={data.day.salesComparison}
                    label={t('dashboard.kpi.vsPreviousDay')}
                  />
                  <ServiceChargeHint amountMd={data.day.serviceChargeMd} money={money} />
                </>
              }
            />
            <StatTile
              label={t('dashboard.kpi.monthlySales')}
              value={money(data.month.salesMd)}
              emphasised={period === 'MONTH'}
              hint={
                <>
                  <ComparisonHint
                    comparison={data.month.salesComparison}
                    label={t('dashboard.kpi.vsPreviousMonth')}
                  />
                  <ServiceChargeHint amountMd={data.month.serviceChargeMd} money={money} />
                </>
              }
            />
            <StatTile
              label={t('dashboard.kpi.reservationSales')}
              value={money(active.reservationSalesMd)}
              hint={periodLabel}
            />
            <StatTile
              label={t('dashboard.kpi.cafeSales')}
              value={money(active.cafeSalesMd)}
              hint={periodLabel}
            />
            {/*
              Owner request, 2026-09-20: "i want to be able to see how much we
              have given discounts".

              Straight after the two sales lines it came off, because that is
              the sentence it belongs to — this is what we rang up, this is what
              came off it. Its hint is the SHARE of sales rather than the period,
              which is the reading that means something: 200,000 given away is a
              different fact on a 300,000 day than on a 3,000,000 one.

              Toned like a deduction, the mirror of refills reading green. It is
              not an alarm — giving a discount is a decision, not a fault — but
              it is the one figure on this row where up is not good news.
            */}
            <StatTile
              label={t('dashboard.kpi.discountsGiven')}
              value={money(active.discountsMd)}
              tone="danger"
              hint={
                // Null when gross sales are not positive — a share of nothing
                // is unknown, and printing 0% would read as "we gave nothing".
                active.discountPercentOfSales === null
                  ? periodLabel
                  : t('dashboard.kpi.discountsShare', {
                      percent: active.discountPercentOfSales,
                      period: periodLabel,
                    })
              }
            />
            {/* Net of balance refunds (owner, 2026-10-14); the hint says how. */}
            <RefillsTile summary={active} periodLabel={periodLabel} money={money} />
          </div>

          {/* Owner request, 2026-09-23: how much cash is in the drawer right
              now. Straight under the money it came from, above the floor, so
              it is on the first screen of a phone. Since 2026-10-12 that is
              the venue's one drawer, with the warning when nobody counted it
              across a business day; until the owner starts it, the boxes
              panel still answers (the server sends one or the other). Null —
              and so absent — for a viewer who may not see it. */}
          {data.cashDrawer?.enabled ? (
            <CashDrawerPanel drawer={data.cashDrawer} money={money} />
          ) : (
            <CashInBoxesPanel summary={data.cashInBoxes} money={money} />
          )}

          {/* The live floor, between the money and the chart (card order). */}
          <FloorGrid occupancy={data.occupancy} devices={data.floorDevices} />

          {noTradeYet ? (
            <p className="text-xs text-content-muted">{t('dashboard.noTradeYet')}</p>
          ) : null}

          <div className="grid min-w-0 gap-3.5 xl:grid-cols-[1.7fr_1fr]">
            <div className="min-w-0 space-y-3.5">
              <Panel
                title={t('dashboard.chart.title')}
                /* The toggle rides both heads it re-buckets: this chart and
                   the usage panel below share one period. */
                actions={<PeriodToggle period={period} onChange={setPeriod} />}
              >
                <SalesChart
                  series={data.salesSeries}
                  period={data.period}
                  currencySymbol={currencySymbol}
                />
              </Panel>

              <MonthlyTrendPanel series={data.monthlySales} money={money} />

              <DeviceUsagePanel
                rows={data.deviceUsage}
                money={money}
                /* The owner asked for the daily/monthly switch HERE, where he
                   reads utilisation — same period state as the chart's. */
                actions={<PeriodToggle period={period} onChange={setPeriod} />}
              />
            </div>

            <div className="space-y-3.5">
              <TargetPanel target={data.monthlyTarget} money={money} />
              <TopDevicesPanel rows={data.topDevices} money={money} />
              <TopCustomersPanel customers={data.topCustomers} money={money} />
              <LowStockPanel items={data.lowStock} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * First-load failure.
 *
 * The server's own message is shown as secondary detail: a 404 while the
 * endpoint is still being built and a 403 from a mis-set permission look
 * identical otherwise, and the person reading this screen is usually the one
 * who can act on the difference.
 */
/** The DAY/MONTH switch — one component so its two mounts cannot drift. */
function PeriodToggle({
  period,
  onChange,
}: {
  period: DashboardPeriod;
  onChange: (period: DashboardPeriod) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {DASHBOARD_PERIODS.map((entry) => (
        <button
          key={entry}
          type="button"
          onClick={() => onChange(entry)}
          aria-pressed={period === entry}
          className={[
            'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            period === entry
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-subtle text-content-secondary hover:bg-hover',
          ].join(' ')}
        >
          {t(entry === 'DAY' ? 'dashboard.period.day' : 'dashboard.period.month')}
        </button>
      ))}
    </>
  );
}

function ErrorState({ detail, onRetry }: { detail: string; onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="p-4">
      <div role="alert" className="rounded-card border border-danger/40 bg-raised p-4 text-sm">
        <p className="font-medium text-danger">{t('dashboard.error.title')}</p>
        <p className="mt-1 text-content-secondary">{t('dashboard.error.body')}</p>
        <p className="mt-1 text-xs text-content-muted">{detail}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-full border border-subtle px-4 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          {t('dashboard.error.retry')}
        </button>
      </div>
    </div>
  );
}

/** Rejects the epoch stamp `emptyDashboardPayload` uses for "never computed". */
function isPlausibleInstant(iso: string): boolean {
  const at = new Date(iso);
  return !Number.isNaN(at.getTime()) && at.getUTCFullYear() > 2000;
}
